<template>
	<div class="billing-location" :class="{ 'billing-location--show': showBillingLocationFields }">
		<div class="billing-location__header">
			<div class="billing-location__header__title">
				{{ $t('checkout.billing-location') }}
			</div>
			<div v-if="!showBillingLocationFields" class="billing-location__header__location">
				{{ headerCountry }}{{ headerZipRegion }}

				<button class="btn-link-blue" @click="displayBillingLocationForms">
					{{ $t('checkout.edit') }}
					<svgIcon icon-holder-class="icon-faq" icon-id="icon-page-right-blue" />
				</button>
			</div>
		</div>
		<div v-if="showBillingLocationFields" class="billing-location__body">
			<div class="countryzip-container">
				<div class="country-select">
					<label>{{ $t('checkout.country') }}</label>
					<FormSelect
						id="billing-country"
						ref="countrySelect"
						v-model="inputCountryCode"
						:has-error="$v.inputCountryCode.$error"
						string-selector="_common.country-list"
						:options="countryCodes"
						:options-value="'countryCode'"
						:options-label="'countryCode'"
						:disabled="disableCountrySelect"
						:place-holder="$t('create-account.select-country')"
						@change="handleCountryChange"
					>
					</FormSelect>
				</div>

				<div v-if="zipCodeRequired" class="zipcode-input">
					<FormInput
						id="billing-code"
						v-model.trim="inputZipRegionCode"
						:has-error="$v.inputZipRegionCode.$error || isInvalidZip"
						:placeholder="
							$t('checkout.zip-code-placeholder', {
								zipCodeNumber: inputCountryCode === 'CA' ? 'A1A 1A1' : '90210',
							})
						"
						:enter-handler="saveBillingLocation"
						:max-length="10"
						:label-text="inputCountryCode === 'CA' ? $t('checkout.province-code') : $t('checkout.zip-code')"
					></FormInput>
				</div>

				<p v-if="(zipCodeRequired && $v.inputZipRegionCode.$error) || isInvalidZip" class="help-block">
					{{
						inputCountryCode === 'CA'
							? $t('error.input.enter-valid-province-code')
							: $t('error.input.billing-info')
					}}
				</p>
			</div>
			<div v-if="citySelectRequired" class="cities-container">
				<label>{{ $t(`checkout.cities.${inputCountryCode}.label`) }}</label>
				<FormSelect
					id="billing-city"
					v-model="$v.inputZipRegionCode.$model"
					:string-selector="`checkout.cities.${inputCountryCode}`"
					:options="selectedCountriesData.regionSelection"
					:has-error="$v.inputZipRegionCode.$error"
					:options-value="'regionCode'"
					:options-label="'regionCode'"
					:place-holder="$t(`checkout.cities.${inputCountryCode}.placeholder`)"
				>
				</FormSelect>
			</div>

			<div class="actions-container">
				<div v-if="showCancelButton" class="btn-container btn-container--cancel">
					<FormButton
						class="btn-primary"
						:label="$t('cancel')"
						:button-click="cancel"
						:disabled="buttonLoading"
					/>
				</div>
				<div class="btn-container btn-container--save">
					<FormButton
						:label="$t('checkout.save-location')"
						:button-click="saveBillingLocation"
						:loading="buttonLoading"
					/>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { FormSelect, FormInput, FormButton } from '@arena/toolkit';
import svgIcon from '@arena/toolkit/src/components/common/icon-svg';
import { required, requiredIf, maxLength } from 'vuelidate/lib/validators';

export default {
	name: 'BillingLocation',
	components: {
		FormSelect,
		FormInput,
		FormButton,
		svgIcon,
	},
	data() {
		return {
			inputCountryCode: null,
			inputZipRegionCode: null,
			disableCountrySelect: false,
			buttonLoading: false,
			pageLoading: false,
			showCancelButton: false,
			isInvalidZip: false,
			isEdit: false,
		};
	},
	validations() {
		return {
			inputCountryCode: {
				required,
			},
			inputZipRegionCode: {
				required: requiredIf(() => this.zipCodeRequired || this.citySelectRequired),
				isValidZip: (value) => {
					return !(this.inputCountryCode === 'US' && !/\b\d{5}\b/g.test(value));
				},
				...(this.inputCountryCode !== 'AE' ? { maxLength: maxLength(10) } : {}),
			},
		};
	},
	computed: {
		savedCountryCode() {
			return this.$store.state.countryCode;
		},
		savedZipCode() {
			return this.$store.state.zipCode;
		},
		countryCodes() {
			return this.$store.state.billingLocationData.countrySelection;
		},
		selectedCountriesData() {
			return this.countryCodes.find((country) => country.countryCode === this.inputCountryCode);
		},
		zipCodeRequired() {
			return (
				this.inputCountryCode &&
				this.selectedCountriesData &&
				this.selectedCountriesData.requiresSelection &&
				this.selectedCountriesData.selectionType === 'text'
			);
		},
		citySelectRequired() {
			return (
				this.inputCountryCode &&
				this.selectedCountriesData &&
				this.selectedCountriesData.requiresSelection &&
				this.selectedCountriesData.selectionType === 'list'
			);
		},
		showBillingLocationFields() {
			return this.$store.state.showBillingLocationFields;
		},
		headerCountry() {
			if (this.$te(`_common.country-list.${this.savedCountryCode}`)) {
				return this.$t(`_common.country-list.${this.savedCountryCode}`);
			}
			return this.savedCountryCode;
		},
		headerZipRegion() {
			if (this.savedCountryCode === 'AE') {
				return this.$te(`checkout.cities.AE.${this.savedZipCode}`)
					? `, ${this.$t(`checkout.cities.AE.${this.savedZipCode}`)}`
					: `, ${this.savedZipCode}`;
			}
			return this.savedZipCode ? `, ${this.savedZipCode}` : '';
		},
		checkoutId() {
			return this.$store.state.checkoutIdSession;
		},
		isIngamePurchase() {
			return this.$route.name === 'in-game-purchase' || this.$store.state.appType === 'game';
		},
	},
	watch: {
		showBillingLocationFields(value) {
			if (!value) {
				this.inputCountryCode = null;
			}
		},
	},
	beforeUpdate() {
		// Only set initial value if inputCountryCode is null
		if (this.countryCodes && this.countryCodes.length > 0 && this.inputCountryCode === null) {
			this.inputCountryCode = this.countryCodes[0].countryCode;
		}
	},
	beforeMount() {
		if (this.countryCodes && this.countryCodes.length > 0) {
			this.inputCountryCode = this.countryCodes[0].countryCode;
		}
	},

	methods: {
		handleCountryChange(value) {
			let objectName = 'location_select';
			if (this.isEdit) {
				objectName = 'edit_location_select';
			}
			this.$a2s.click(objectName, {
				name: 'location_select',
				value,
			});
		},
		displayBillingLocationForms() {
			this.isEdit = true;
			this.$a2s.click('purchase_method_edit_location');
			dataLayer.push({
				event: `${this.isIngamePurchase ? 'In Game' : 'Add Balance'} Edit Billing Location`,
			});

			this.$store.commit('setShowBillingLocationFields', true);
			this.$store.commit('setPgLoading', false);
			this.$store.commit('setKarmakoinSelectedOption', null); // remove karmakoin selected option
			this.inputCountryCode = this.savedCountryCode;
			this.inputZipRegionCode = this.savedZipCode ? this.savedZipCode : null;
			this.showCancelButton = true;
		},
		async saveBillingLocation() {
			this.$v.$touch();
			let objectName = 'location_save';
			if (this.isEdit) {
				objectName = 'edit_location_save';
			}
			this.$a2s.click(objectName);

			this.isEdit = false;
			if (!this.$v.$invalid) {
				this.buttonLoading = true;
				this.isInvalidZip = false;
				try {
					const response = await this.$checkoutAPI.saveBillingLocation(
						this.inputCountryCode,
						this.inputZipRegionCode,
						this.checkoutId
					);
					this.$store.commit('setCountryCode', response.data.billingLocation.countryCode);
					this.$store.commit('setZipCode', response.data.billingLocation.zipCode);
					this.$store.commit('setPurchaseSummaryData', response.data.purchaseSummary);

					dataLayer.push({
						event: `${this.isIngamePurchase ? 'In Game' : 'Add Balance'} Location Submit`,
						'Returning Check': 'Manual',
					});

					if (!this.isIngamePurchase) {
						// only for web add-balance
						dataLayer.push({ ecommerce: null });

						window.dataLayer.push({
							event: 'checkout',
							ecommerce: {
								checkout: {
									actionField: { step: 1 },
								},
							},
						});
					}

					if (!this.showCancelButton) {
						// if it's on verify token and zip code is required then get payment methods and if initiateChannel then start the most recently used PG
						await this.$store.dispatch('fetchPaymentMethodsBeginTransaction');
					} else if (this.$store.state.showSelectedPg) {
						if (this.$store.state.isMinor) {
							this.$store.commit('setShowSelectedPg', false);
						} else {
							// if user clicked on edit button then payment methods already exist in vuex, just need to check if some PG is selected and begin payment with that pg selected
							this.$store.dispatch('beginPayment');
						}
					}
					this.$store.commit('setShowBillingLocationFields', false);
					this.buttonLoading = false;
				} catch (error) {
					this.buttonLoading = false;

					if (error && error.response && error.response.data && error.response.data.code === '002-400-0011') {
						this.isInvalidZip = true;
						return;
					}

					const errorCode =
						error.response && error.response.data && error.response.data.code
							? error.response.data.code
							: error.response.status || '500';

					if (this.isIngamePurchase) {
						this.$router.replace({
							name: 'in-game-purchase-failed',
							query: {
								...(errorCode && { error_code: errorCode }),
							},
						});
					} else {
						this.$router.replace({
							name: 'purchase-failed',
							params: {
								errorCode: errorCode,
							},
						});
					}
				}
			}
		},
		cancel() {
			this.$a2s.click('edit_location_cancel');

			this.$store.commit('setShowBillingLocationFields', false);
			this.$v.$reset();
		},
	},
};
</script>

<style lang="less" scoped>
.billing-location {
	flex: 0 0 100%;
	max-width: 100%;
	border-top: 2px solid rgba(#fff, 50%);
	border-bottom: 2px solid rgba(#fff, 50%);
	color: #fff;
	margin-bottom: 13px;

	&--show {
		border-bottom-color: transparent;
	}
	&__header {
		display: flex;
		flex-wrap: wrap;
		align-items: flex-end;
		font-size: 18px;
		font-weight: bold;
		padding-bottom: 16px;
		&__title {
			margin-right: 20px;
			padding-top: 13px;
		}
		&__location {
			display: flex;
			flex-wrap: nowrap;
			flex-basis: auto;
			flex-grow: 1;
			max-width: 100%;
			justify-content: space-between;
			align-items: center;
			font-size: 14px;
			font-weight: normal;
			white-space: nowrap;
			padding-top: 12px;
			padding-bottom: 1px;
			.btn-link-blue {
				margin-left: auto;
				padding-top: 1px;
				text-transform: uppercase;
				padding-left: 4px;
			}
		}
	}
	&__body {
		display: flex;
		flex-wrap: wrap;
		margin-left: -5px;
		margin-right: -5px;
		margin-bottom: 20px;
		padding-top: 16px;
		justify-content: center;
		label {
			font-size: 14px;
			margin-bottom: 7px;
		}
		.countryzip-container,
		.cities-container {
			flex: 0 0 100%;
			max-width: 100%;
		}
		.countryzip-container {
			display: flex;
			flex-wrap: wrap;
			.country-select,
			.zipcode-input {
				padding-left: 5px;
				padding-right: 5px;
			}
			.country-select {
				flex-basis: 0;
				flex-grow: 1;
				max-width: 100%;
			}
			.zipcode-input {
				flex: 0 0 38%;
				max-width: 38%;
			}
			> .help-block {
				flex: 0 0 100%;
				max-width: 100%;
				margin-top: -5px;
				padding-left: 5px;
				padding-right: 5px;
				color: #e63f57;
			}
		}
		.cities-container {
			padding-top: 6px;
		}
		.country-select,
		.cities-container {
			::v-deep .form-group {
				label {
					display: none;
				}
			}
		}
		.cities-container {
			padding-left: 5px;
			padding-right: 5px;
		}
		.actions-container {
			flex: 0 0 100%;
			max-width: 100%;
			display: flex;
			flex-wrap: wrap;
			justify-content: center;
			margin-left: -5px;
			margin-right: -5px;
			.btn-container {
				flex: 0 0 50%;
				max-width: 50%;
				padding-left: 5px;
				padding-right: 5px;
			}
			.btn-block {
				margin-top: 10px;
				max-width: 100%;
			}
		}
		::v-deep .form-group {
			label {
				font-size: 14px;
				margin-bottom: 7px;
			}
			&.mobile {
				height: initial;
				margin-top: 0;
				.caret {
					top: 50%;
				}
			}
			.form-control {
				position: relative;
				.caret {
					top: 50%;
				}
			}
		}
	}
}
@media (max-width: 599px) {
	.billing-location {
		margin-bottom: 8px;
		&__header {
			padding-bottom: 6px;
			&__title {
				flex: 0 0 100%;
				max-width: 100%;
				padding-top: 8px;
				margin-right: 0;
			}
			&__location {
				flex: 0 0 100%;
				max-width: 100%;
				padding-top: 0px;
				padding-top: 13px;
			}
		}
		&__body {
			margin-left: 0;
			margin-right: 0;
			padding-top: 7px;
			.countryzip-container {
				.country-select,
				.zipcode-input {
					padding-left: 0;
					padding-right: 0;
				}
				.country-select {
					flex: 0 0 100%;
					max-width: 100%;
				}
				.zipcode-input {
					flex: 0 0 100%;
					max-width: 100%;
				}
				> .help-block {
					padding-left: 0;
					padding-right: 0;
				}
			}
			.cities-container {
				padding-left: 0;
				padding-right: 0;
			}
			.actions-container {
				margin-left: 0;
				margin-right: 0;
				.btn-container {
					padding-left: 0;
					padding-right: 0;
					flex: 0 0 100%;
					max-width: 100%;
					&--save {
						order: -1;
					}
				}
				.btn-block {
					margin-top: 10px;
				}
			}
		}
	}
}
</style>
